import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from "react-router-dom";
import { I18nextProvider } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from "react-query";
import { AuthProvider } from "./context/AuthContext";
import { ChatbotProvider } from "./context/ChatbotContext";
import i18n from './i18n';
import Header from "./components/Header";
import Footer from "./components/Footer";
import ChatBot from "./components/ChatBot/ChatBot";
import ChatBotRestoreButton from './components/ChatBot/ChatBotRestoreButton';
import Home from "./Home";
import Jobs from "./Jobs";
import Employers from "./Employers";
import About from "./About";
import Contact from "./Contact";
import EmployerContact from "./EmployerContact";
import GeneralApplication from "./GeneralApplication";
import JobDetails from "./JobDetails";
import ApplicationForm from "./ApplicationForm";
import PostJob from "./PostJob";
import UserLogin from "./UserLogin";
import AdminDashboard from "./AdminDashboard";
import AdminJobListings from "./AdminJobListings";
import AdminJobView from "./AdminJobView";
import AdminApplications from "./AdminApplications";
import EditJob from "./EditJob";
import ApplicationDetails from "./ApplicationDetails";
import ProtectedRoute from "./components/ProtectedRoute";
import NotFound from "./NotFound";
import QuickActions from "./QuickActions";
import Analytics from "./Analytics";
import TeamPage from "./TeamPage";
import EmployeePortal from "./EmployeePortal";
import EmployeeManual from "./EmployeeManual";
import EmployeeJobs from "./EmployeeJobs";
import SavedJobs from "./SavedJobs";
import AdminNotifications from "./AdminNotifications";
import TermsAndConditions from "./TermsAndConditions";
import PrivacyPolicy from "./PrivacyPolicy";
import Resignation from "./Resignation";
import EmployeeRelations from "./EmployeeRelations";
import HolidaysAndPTO from "./HolidaysAndPTO";
import ExactWorkersCompNoticeBilingual from "./ExactWorkersCompNoticeBilingual";
import EmployeeNoticeViewer from "./EmployeeNoticeViewer";
import DirectHires from "./DirectHires";
import TemporaryToHire from "./TemporaryToHire";
import TemporaryEmployees from "./TemporaryEmployees";
import AdministrativeService from "./AdministrativeService";
import ContractEmployees from "./ContractEmployees";
import StaffAugmentation from "./StaffAugmentation";
import DocumentsPage from "./DocumentsPage";
import Services from "./Services";
import AdminEmployerContacts from "./AdminEmployerContacts";
import CustomerService from "./components/CustomerService";
import DirectoryPage from './pages/DirectoryPage';
import ReferralProgram from './pages/ReferralProgram';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import AdminBlog from './pages/AdminBlog';
import AdminGeneralInquiries from './AdminGeneralInquiries';
import AdminReferrals from './pages/AdminReferrals';
import AdminOnboarding from './pages/AdminOnboarding';
import AdminResignations from './pages/AdminResignations';
import OnboardingForms from './pages/OnboardingForms';
import TrainingPage from './pages/TrainingPage';
import AdminW2Upload from './pages/AdminW2Upload';
import AdminW2Documents from './pages/AdminW2Documents';
import W2Page from './pages/W2Page';
import AdminLogsPage from './pages/AdminLogsPage';
import AdminBulletins from './pages/AdminBulletins';
import AdminBanners from './pages/AdminBanners';
import AdminTrainingPage from "./pages/AdminTrainingPage";
import AdminSettings from "./pages/AdminSettings";
import Announcements from './pages/Announcements';
import EmployeeAnnouncements from './pages/EmployeeAnnouncements';
import AdminPaystubManagement from './AdminPaystubManagement';

const queryClient = new QueryClient();

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ChatbotProvider>
              <Router>
                <div className="flex flex-col min-h-screen">
                  <Header />
                  <main className="flex-1">
                    <Routes>
                      {/* Public routes */}
                      <Route path="/" element={<Employers />} />
                      <Route path="/home" element={<Home />} />
                      <Route path="/employers" element={<Navigate to="/" replace />} />
                      <Route path="/jobs" element={<Jobs />} />
                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog/:id" element={<BlogPost />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/employer-contact" element={<EmployerContact />} />
                      <Route path="/customer-service" element={<CustomerService />} />
                      <Route path="/onboarding" element={<OnboardingForms />} />
                      <Route
                        path="/general-application"
                        element={<GeneralApplication />}
                      />
                      <Route path="/job/:id" element={<JobDetails />} />
                      <Route path="/apply/:jobId" element={<ApplicationForm />} />
                      <Route path="/post-job" element={<PostJob />} />
                      <Route path="/login" element={<UserLogin />} />
                      <Route path="/team" element={<TeamPage />} />
                      <Route path="/terms" element={<TermsAndConditions />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />

                      {/* Service routes */}
                      <Route path="/services/direct-hires" element={<DirectHires />} />
                      <Route
                        path="/services/temporary-to-hire"
                        element={<TemporaryToHire />}
                      />
                      <Route
                        path="/services/temporary-employees"
                        element={<TemporaryEmployees />}
                      />
                      <Route
                        path="/services/administrative-service"
                        element={<AdministrativeService />}
                      />
                      <Route
                        path="/services/contract-employees"
                        element={<ContractEmployees />}
                      />
                      <Route
                        path="/services/staff-augmentation"
                        element={<StaffAugmentation />}
                      />
                      <Route path="/services" element={<Services />} />

                      {/* Admin routes */}
                      <Route
                        path="/admin-dashboard"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/job-listings"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminJobListings />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/jobs/:id"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminJobView />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/applications"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminApplications />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/edit-job/:id"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <EditJob />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/application/:id"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <ApplicationDetails />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/applications/:id"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <ApplicationDetails />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/quick-actions"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <QuickActions />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/analytics"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <Analytics />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/notifications"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminNotifications />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/employer-contacts"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminEmployerContacts />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/general-inquiries"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminGeneralInquiries />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/referrals"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminReferrals />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/onboarding"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminOnboarding />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/resignations"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminResignations />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/w2-upload"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminW2Upload />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/w2-documents"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminW2Documents />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/bulletins"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminBulletins />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/banners"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminBanners />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/logs"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminLogsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/blog"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminBlog />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/training"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminTrainingPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/settings"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminSettings />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/paystubs"
                        element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminPaystubManagement />
                          </ProtectedRoute>
                        }
                      />

                      {/* Protected employee routes */}
                      <Route
                        path="/employee-portal"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <EmployeePortal />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/referrals"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <ReferralProgram />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/employee/saved-jobs"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <SavedJobs />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/employee/documents"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <DocumentsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/employee/manual"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <EmployeeManual />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/employee/w2"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <W2Page />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/employee/resignation"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <Resignation />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/employee-relations"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <EmployeeRelations />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/holidays-and-pto"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <HolidaysAndPTO />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/employee-jobs"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <EmployeeJobs />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/employee/training"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <TrainingPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/employee/notices/:noticeId"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <EmployeeNoticeViewer />
                          </ProtectedRoute>
                        }
                      />

                      {/* Workers' Compensation Notice */}
                      <Route
                        path="/employee/workers-comp-notice"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <ExactWorkersCompNoticeBilingual />
                          </ProtectedRoute>
                        }
                      />
                      
                      {/* Safety Posters */}
                      <Route
                        path="/employee/safety-poster/english"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <Navigate to="/employee/notices/employee-safety-health-english" replace />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/employee/safety-poster/spanish"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <Navigate to="/employee/notices/employee-safety-health-spanish" replace />
                          </ProtectedRoute>
                        }
                      />

                      {/* Exposure Notices */}
                      <Route
                        path="/employee/exposure-notice/english"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <Navigate to="/employee/notices/mrsa-meningitis-tb-exposure-english" replace />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/employee/exposure-notice/spanish"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <Navigate to="/employee/notices/bodily-fluids-exposure-spanish" replace />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/employee/bodily-fluids-notice/english"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <Navigate to="/employee/notices/bodily-fluids-exposure-english" replace />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/directory"
                        element={
                          <ProtectedRoute allowedRoles={["employee", "admin", "supervisor"]}>
                            <DirectoryPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Announcements */}
                      <Route path="/announcements" element={<Announcements />} />

                      {/* Employee Announcements */}
                      <Route path="/employee/announcements" element={<EmployeeAnnouncements />} />

                      {/* Catch-all route for 404 */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </main>
                  {localStorage.getItem('hideChatBot') !== 'true' ? <ChatBot /> : <ChatBotRestoreButton />}
                  <Footer />
                </div>
              </Router>
            </ChatbotProvider>
          </AuthProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </I18nextProvider>
  );
}

export default App;
